require('@lottiefiles/lottie-player');
import { AfterViewInit, Component, CUSTOM_ELEMENTS_SCHEMA, ElementRef, EventEmitter, Input, NgZone, OnDestroy, OnInit, Output } from '@angular/core';
import { fromEvent, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import get from 'lodash-es/get';
import isUndefined from 'lodash-es/isUndefined';

import { LottieAnimation, LottieSettings } from './lottie.interface';
import { ViewportDetectionService } from '@finder/shared/services/viewport-detection/viewport-detection.service';
import { DEVICE_TYPE } from '@finder/shared/services/viewport-detection/device-type.constant';
import { A11yService } from '@finder/shared/services/a11y/a11y.service';
import { DebugLogService } from '@finder/shared/services/debug-log/debug-log.service';
import { CommonModule } from '@angular/common';

@Component({
    selector: 'finder-lottie',
    templateUrl: './lottie.component.html',
    styleUrls: ['./lottie.component.scss'],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    standalone: true,
    imports: [CommonModule],
})
export class LottieComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input() data: LottieSettings;
    @Input() playback: Observable<boolean>;
    @Output() reference = new EventEmitter<any>();

    style: any;
    lottieSrc: string;

    get animation() {
        return this.el.nativeElement.querySelector('lottie-player');
    }

    private destroy$ = new Subject<void>();
    private reducedMotionEnabled = false;
    private disabled = false;
    private lottie: LottieAnimation;

    constructor(
        private el: ElementRef,
        private viewport: ViewportDetectionService,
        private a11yService: A11yService,
        private debugLog: DebugLogService,
        private ngZone: NgZone,
    ) {}

    ngOnInit() {
        if (!this.data) {
            return;
        }

        // Set lottie src
        this.init();

        // Update the css vars
        this.setCss();
    }

    ngAfterViewInit() {
        // Subscribe for playback triggering
        const autoplay = get(this.data, 'autoplay');
        if (!isUndefined(autoplay) && autoplay !== true) {
            this.setPlayback();
        }

        // Add listener (if applicable)
        if (this.animation) {
            this.addAnimationListener();
        }

        // Emit a reference for anything that needs it
        this.reference.emit(this);
    }

    // Ignore just for coverage, there's no need to test unsubscribe
    /* istanbul ignore next */
    ngOnDestroy() {
        // Unfortunately test complain about destroy, so we also need to check it exists
        if (this.animation && this.animation.destroy) {
            this.animation.destroy();
        }

        this.destroy$.next();
        this.destroy$.complete();
    }

    private init() {
        this.reducedMotionEnabled = this.a11yService.reducedMotionEnabled();
        if (this.reducedMotionEnabled) {
            // Remove properties
            delete this.data.autoplay;
            delete this.data.loop;
        }

        if (!this.data.mobileSrc) {
            this.lottieSrc = this.data.src;

            return;
        }

        // Set current src
        this.setSrc(this.viewport.getDevice());

        // Listen for changes
        this.viewport.onViewportResize()
            .pipe(
                takeUntil(this.destroy$)
            ).subscribe((viewport: DEVICE_TYPE) => {
                this.setSrc(viewport);
            });
    }

    private setSrc(viewport) {
        this.lottieSrc = viewport === DEVICE_TYPE.mobile ?
            this.data.mobileSrc :
            this.data.src;
    }

    private setPlayback() {
        // Check for loop frames - If we have specific loop frames do not proceed
        if (this.setPlaybackLoop()) {
            return;
        }

        /* istanbul ignore if */
        if (!this.playback) {
            return;
        }

        this.playback.subscribe(value => {
            if (value !== true || !this.animation) {
                return;
            }

            // If we are just looping the entire lottie, simply play...
            this.animation.play();
        });
    }

    private setPlaybackLoop() {
        if (this.disabled) {
            return false;
        }

        const { frames, loopStart } = this.data;

        this.debugOut(`setPlaybackLoop - frames: ${frames}, loopStart: ${loopStart}`);

        if (loopStart) {
            setTimeout(() => {
                // Get lottie - the check is mainly for unit testing so we can override
                /* istanbul ignore if */
                if (!this.lottie) {
                    this.lottie = get(this.animation, '_lottie');
                }

                this.ngZone.runOutsideAngular(() => {
                    // Set loop playback
                    this.lottie.playSegments([0, frames], true);
                    this.lottie.addEventListener('complete', () => {

                        this.debugOut('lottie done!');

                        this.lottie.goToAndPlay(loopStart, true);
                    });
                });
            });

            return true;
        }

        return false;
    }

    private setCss() {
        ['top', 'right', 'bottom', 'left', 'width', 'height'].forEach(key => {
            if (this.data[key]) {
                this.el.nativeElement.style.setProperty(`--finder-lottie-player-${key}`, this.data[key]);
            }
        });
    }

    private addAnimationListener() {
        // Listen to load event
        fromEvent(this.animation, 'load')
            .pipe(takeUntil(this.destroy$))
            .subscribe(/* istanbul ignore next */() => {
                const lottie = get(this.animation, '_lottie');
                if (!lottie) {
                    return;
                }

                this.checkMediaQuery(lottie);
            });
    }

    private checkMediaQuery(lottie) {
        // Check if the guest has `prefers-reduced-motion` enabled
        if (!this.reducedMotionEnabled) {
            return;
        }

        // Add a new flag to the lottie so any consumers can honor
        lottie.disabled = this.disabled = true;

        // Go to the end and don't bother playing back
        lottie.goToAndStop(lottie.totalFrames - 1, true);

        // Let's add a log output just in case anyone wonders why lottie's aren't playing
        this.debugOut('Lottie playback disabled by "reduced motion" OS setting.');
    }

    private debugOut(msg, logType = 'log') {
        this.debugLog[logType](`***** Lottie - ${msg}`);
    }
}
